<template>
  <section class="space-y-5">
    <div class="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div
        v-if="isLoading"
        class="flex flex-wrap items-start justify-between gap-3"
        aria-label="Loading payment method"
      >
        <div class="min-w-0 flex-1 space-y-3">
          <div class="payment-method-skeleton h-4 w-28 rounded" />
          <div class="payment-method-skeleton h-11 max-w-[240px] rounded-md" />
          <div class="payment-method-skeleton h-3 w-44 rounded" />
        </div>
        <div class="min-w-[90px] space-y-3">
          <div class="payment-method-skeleton ml-auto h-7 w-20 rounded-full" />
          <div class="payment-method-skeleton ml-auto h-3 w-24 rounded" />
        </div>
      </div>
      <div
        v-else
        class="flex flex-wrap items-start justify-between gap-3"
      >
        <div class="min-w-0 flex-1">
          <p class="text-sm font-semibold text-zinc-800">
            Card on file
          </p>
          <div
            v-if="hasCardOnFile"
            class="mt-2 inline-flex max-w-full flex-col rounded-md border border-emerald-300 bg-white px-3 py-2"
          >
            <p class="truncate text-sm font-semibold text-zinc-800">
              {{ cardDisplayLabel }}
            </p>
          </div>
          <p
            v-else
            class="mt-1 text-sm text-zinc-500"
          >
            No active card on file.
          </p>
          <p
            v-if="billingSummaryText"
            class="mt-1 text-xs text-zinc-500"
          >
            {{ billingSummaryText }}
          </p>
        </div>

        <div class="min-w-0 text-right">
          <span
            :class="hasCardOnFile ? 'bg-emerald-100 text-emerald-700' : 'bg-zinc-100 text-zinc-600'"
            class="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
          >
            {{ hasCardOnFile ? 'Active' : 'Inactive' }}
          </span>
          <p
            v-if="lastUsedText"
            class="mt-2 max-w-full truncate text-xs text-zinc-500"
          >
            Last used {{ lastUsedText }}
          </p>
        </div>
      </div>
    </div>

    <div class="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm">
      <div
        v-if="isLoading"
        class="space-y-3"
        aria-label="Loading payment data"
      >
        <div class="payment-method-skeleton h-4 w-36 rounded" />
        <div class="payment-method-skeleton h-16 w-full rounded-md" />
        <div class="payment-method-skeleton h-16 w-full rounded-md" />
      </div>
      <div
        v-else
        class="space-y-4"
      >
        <div>
          <p class="text-sm font-semibold text-zinc-800">
            Payment data on file
          </p>
          <p class="mt-1 text-xs text-zinc-500">
            Local database values and Stripe account references for this member.
          </p>
        </div>

        <div
          v-if="!paymentDiagnosticSections.length"
          class="rounded-md border border-zinc-100 bg-zinc-50 px-3 py-3 text-sm text-zinc-500"
        >
          No payment identifiers are stored for this member yet.
        </div>

        <div
          v-for="section in paymentDiagnosticSections"
          :key="section.key"
          class="rounded-lg border border-zinc-100 bg-zinc-50 px-3 py-3"
        >
          <div class="flex flex-wrap items-center justify-between gap-2">
            <p class="text-xs font-semibold uppercase tracking-wide text-zinc-500">
              {{ section.title }}
            </p>
            <span
              v-if="section.badge"
              class="rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold text-zinc-500 ring-1 ring-zinc-200"
            >
              {{ section.badge }}
            </span>
          </div>
          <dl class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
            <div
              v-for="item in section.items"
              :key="item.label"
              class="min-w-0"
            >
              <dt class="text-[11px] font-medium uppercase tracking-wide text-zinc-400">
                {{ item.label }}
              </dt>
              <dd
                :class="item.mono ? 'font-mono text-[12px]' : 'text-sm'"
                class="mt-0.5 break-all text-zinc-700"
              >
                {{ item.value || 'Not stored' }}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>

    <div
      v-if="errorMessage"
      class="rounded-lg border border-rose-200 bg-rose-50 px-3 py-3 text-sm text-rose-600"
    >
      {{ errorMessage }}
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue';

const props = defineProps({
  memberId: {
    type: [String, Number],
    default: null,
  },
});

const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL;
const apiBackendUrl = process.env.VUE_APP_PLATFORMA_API_BACKEND_URL;

const isLoading = ref(false);
const errorMessage = ref('');
const paymentMethod = ref(null);
const paymentData = ref(null);

const requestBases = computed(() => {
  const rows = Array.from(new Set([apiProxyUrl, apiBackendUrl].map((value) => String(value || '').trim()).filter(Boolean)));
  return rows.length ? rows : [''];
});

const hasCardOnFile = computed(() => Boolean(paymentMethod.value?.hasCardOnFile));

const cardDisplayLabel = computed(() => {
  const brand = String(paymentMethod.value?.cardBrand || '').trim();
  const last4 = String(paymentMethod.value?.cardLast4 || '').replace(/\D/g, '').slice(-4);
  const month = Number(paymentMethod.value?.cardExpMonth || 0);
  const year = Number(paymentMethod.value?.cardExpYear || 0);
  const mm = month > 0 ? String(month).padStart(2, '0') : '';
  const yy = year > 0 ? String(year).slice(-2) : '';
  const expText = mm && yy ? ` | exp ${mm}/${yy}` : '';
  return `${brand || 'Card'} **** ${last4 || '----'}${expText}`;
});

const billingSummaryText = computed(() => {
  const name = String(paymentMethod.value?.billingName || '').trim();
  const email = String(paymentMethod.value?.billingEmail || '').trim();
  if (name && email) return `${name} | ${email}`;
  if (name) return name;
  if (email) return email;
  return '';
});

const lastUsedText = computed(() => {
  const value = paymentMethod.value?.lastUsedAt;
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString();
});

const formatProviderLabel = (value) => {
  const token = String(value || '').trim().toLowerCase();
  if (token === 'creator_stripe') return 'Creator connected Stripe';
  if (token === 'platforma_payments') return 'Platforma Stripe';
  if (token === 'bitcoin') return 'Bitcoin';
  return value || 'Payment account';
};

const formatCardSummary = (entry = {}) => {
  const brand = String(entry?.cardBrand || '').trim();
  const last4 = String(entry?.cardLast4 || '').replace(/\D/g, '').slice(-4);
  const month = Number(entry?.cardExpMonth || 0);
  const year = Number(entry?.cardExpYear || 0);
  const exp = month && year ? ` exp ${String(month).padStart(2, '0')}/${String(year).slice(-2)}` : '';
  if (!brand && !last4 && !exp) return '';
  return `${brand || 'Card'} ${last4 ? `**** ${last4}` : ''}${exp}`.trim();
};

const compactRows = (rows) => rows.filter((row) => String(row?.value || '').trim());

const paymentDiagnosticSections = computed(() => {
  const data = paymentData.value && typeof paymentData.value === 'object' ? paymentData.value : {};
  const sections = [];
  const resolved = data.resolvedDisplaySource || null;
  if (resolved) {
    sections.push({
      key: 'resolved-display-source',
      title: 'Displayed card source',
      badge: formatProviderLabel(resolved.provider),
      items: compactRows([
        { label: 'Stripe customer ID', value: resolved.stripeCustomerId, mono: true },
        { label: 'Stripe payment method/source ID', value: resolved.stripePaymentMethodId, mono: true },
        { label: 'Merchant account ref', value: resolved.providerAccountRef, mono: true },
        { label: 'Lookup source', value: resolved.source || 'local_saved_method' },
      ]),
    });
  }

  const local = data.localSavedPaymentMethod || null;
  if (local) {
    sections.push({
      key: 'local-saved-method',
      title: 'Local saved payment method',
      badge: local.status || '',
      items: compactRows([
        { label: 'Stripe customer ID', value: local.stripeCustomerId, mono: true },
        { label: 'Stripe payment method ID', value: local.stripePaymentMethodId, mono: true },
        { label: 'Card', value: formatCardSummary(local) },
        { label: 'Payment method type', value: local.paymentMethodType },
        { label: 'Status', value: local.status },
        { label: 'Last used', value: local.lastUsedAt ? new Date(local.lastUsedAt).toLocaleString() : '' },
      ]),
    });
  }

  const links = Array.isArray(data.paymentAccountLinks) ? data.paymentAccountLinks : [];
  links.forEach((link, index) => {
    sections.push({
      key: `account-link-${link.id || index}`,
      title: `${formatProviderLabel(link.provider)} link`,
      badge: link.status || '',
      items: compactRows([
        { label: 'Creator merchant account', value: link.creatorPaymentAccountName || link.connectedAccountRef },
        { label: 'Connected account ref', value: link.connectedAccountRef, mono: true },
        { label: 'Platforma customer ID', value: link.platformStripeCustomerId, mono: true },
        { label: 'Connected customer ID', value: link.connectedStripeCustomerId, mono: true },
        { label: 'Platforma payment method ID', value: link.platformStripePaymentMethodId, mono: true },
        { label: 'Connected payment method ID', value: link.connectedStripePaymentMethodId, mono: true },
        { label: 'Card', value: formatCardSummary(link) },
        { label: 'Updated', value: link.updatedAt ? new Date(link.updatedAt).toLocaleString() : '' },
      ]),
    });
  });

  const accounts = Array.isArray(data.creatorPaymentAccounts) ? data.creatorPaymentAccounts : [];
  accounts.forEach((account, index) => {
    sections.push({
      key: `creator-account-${account.id || index}`,
      title: formatProviderLabel(account.provider),
      badge: account.isDefault ? 'Default' : account.status,
      items: compactRows([
        { label: 'Merchant account', value: account.displayName },
        { label: 'Provider account ref', value: account.providerAccountRef, mono: true },
        { label: 'External account tag', value: account.externalAccountTag, mono: true },
        { label: 'Status', value: account.status },
      ]),
    });
  });

  return sections.filter((section) => Array.isArray(section.items) && section.items.length);
});

const loadPaymentMethod = async () => {
  const memberId = Number(props.memberId || 0);
  if (!Number.isInteger(memberId) || memberId <= 0) {
    paymentMethod.value = null;
    paymentData.value = null;
    return;
  }

  const query = new URLSearchParams({ page: '1', pageSize: '10' });
  const requestPaths = [
    `/api/members/${encodeURIComponent(String(memberId))}/subscriptions/payment-method-events?${query.toString()}`,
    `/api/creators/members/${encodeURIComponent(String(memberId))}/subscriptions/payment-method-events?${query.toString()}`,
  ];
  let response = null;
  let payload = {};
  let lastError = '';

  for (const base of requestBases.value) {
    for (const path of requestPaths) {
      response = await fetch(`${base}${path}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      payload = await response.json().catch(() => ({}));
      if (response.ok && payload?.success !== false) {
        paymentMethod.value = payload?.paymentMethod && typeof payload.paymentMethod === 'object'
          ? payload.paymentMethod
          : null;
        paymentData.value = payload?.paymentData && typeof payload.paymentData === 'object'
          ? payload.paymentData
          : null;
        return;
      }
      lastError = String(payload?.message || payload?.error || `Failed loading payment method (${response.status})`);
      if (response.status !== 404) break;
    }
    if (response?.ok && payload?.success !== false) break;
  }

  throw new Error(lastError || `Failed loading payment method (${response?.status || 500})`);
};

const loadData = async () => {
  isLoading.value = true;
  errorMessage.value = '';
  try {
    await loadPaymentMethod();
  } catch (error) {
    errorMessage.value = String(error?.message || 'Failed loading payment methods data.');
  } finally {
    isLoading.value = false;
  }
};

watch(
  () => props.memberId,
  () => {
    void loadData();
  },
  { immediate: true }
);
</script>

<style scoped>
.payment-method-skeleton {
  position: relative;
  overflow: hidden;
  background: #f4f4f5;
}

.payment-method-skeleton::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.72), transparent);
  animation: payment-method-shimmer 1.15s infinite;
}

@keyframes payment-method-shimmer {
  100% {
    transform: translateX(100%);
  }
}
</style>
